import React, { useEffect, useRef, useState } from 'react';

import { BarChart, LineChart } from 'echarts/charts';
import { DataZoomComponent, GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';

import {
  Box,
  Button,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import RegionalRanking from './RegionalRanking';
import type { SettledComms } from './liveDataAdapter';
import { type Row, formatted, number } from './liveModel';
import { reportPalette } from './palette';
import {
  type OfferSelection,
  WORKSHEET_ROLES,
  linkedOfferRows,
  worksheetCsv,
  worksheetMetrics,
} from './worksheetModel';

echarts.use([BarChart, LineChart, DataZoomComponent, GridComponent, LegendComponent, TooltipComponent, CanvasRenderer]);

const TITLE_ACRONYMS: Record<string, string> = { gm: 'GM', ttv: 'TTV', utm: 'UTM', id: 'ID', lp: 'LP' };

/** Sentence-case column title from a field key, keeping acronyms upper case (gm_per_click -> GM per click). */
function columnTitle(key: string): string {
  return key
    .split('_')
    .map((word, i) => TITLE_ACRONYMS[word] ?? (i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');
}

const money = (key: string) => ['gm', 'ttv'].includes(key);
export type WorksheetContext = {
  identity: string;
  ready: boolean;
  selections: Record<OfferSelection['source'], OfferSelection | null>;
  metric: string;
  sections: Array<Row>;
};
const OPTION_LABELS: Record<string, string> = { vendor_name: 'Vendor Name', offer_name: 'Offer Name' };
/** Wide enough for the whole label at the 11px control font, so no label is cut short. */
const controlWidth = (label: string) => Math.max(130, label.length * 7 + 32);
/** Reduce additive operands at the source mark keys, then derive ratios; never sum a ratio. */
export function worksheetMarks(rows: Array<Row>, line: boolean): Array<Row> {
  const groups = new Map<string, Row>();
  for (const row of rows) {
    const key = JSON.stringify(line ? [row.bucket, row.split] : [row.split, row.detail]),
      group = groups.get(key) ?? { bucket: row.bucket, split: row.split, detail: row.detail };
    for (const field of ['total_clicks', 'unique_clicks', 'items', 'ttv', 'gm']) {
      const n = number(row[field]);
      if (n !== null) {
        group[field] = (number(group[field]) ?? 0) + n;
      }
    }
    groups.set(key, group);
  }
  return [...groups.values()];
}
function WorksheetChart({
  rows,
  metric,
  line,
  onSelect,
  unique,
}: {
  rows: Array<Row>;
  metric: string;
  line: boolean;
  onSelect?: (row: Row) => void;
  unique: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ref.current) {
      return;
    }
    const chart = echarts.init(ref.current),
      marks = worksheetMarks(rows, line).map((row) => worksheetMetrics(row, unique));
    const categories = line
      ? [...new Set(marks.map((row) => String(row.bucket)))].sort()
      : [...marks]
          .sort((a, b) => (number(b[metric]) ?? -Infinity) - (number(a[metric]) ?? -Infinity))
          .map((row) => String(row.split ?? 'Unknown'));
    const groups = line ? [...new Set(marks.map((row) => String(row.split ?? 'Unknown')))] : ['Value'];
    const ordered = line
      ? marks
      : [...marks].sort((a, b) => (number(b[metric]) ?? -Infinity) - (number(a[metric]) ?? -Infinity));
    chart.setOption({
      animation: false,
      color: [reportPalette.teal, reportPalette.mint, '#5E8C92', '#A9C3C6'],
      tooltip: { trigger: 'axis', confine: true },
      legend: { show: line && groups.length > 1, type: 'scroll' },
      grid: { left: line ? 65 : 180, right: 24, top: line ? 40 : 12, bottom: 40 },
      dataZoom:
        !line && ordered.length > 50
          ? [
              { type: 'inside', yAxisIndex: 0, startValue: 0, endValue: 49, filterMode: 'none' },
              { type: 'slider', yAxisIndex: 0, startValue: 0, endValue: 49, filterMode: 'none', right: 0, width: 12 },
            ]
          : [],
      xAxis: line ? { type: 'category', data: categories } : { type: 'value', name: money(metric) ? 'AUD' : 'Clicks' },
      yAxis: line
        ? { type: 'value', name: money(metric) ? 'AUD' : 'Clicks' }
        : { type: 'category', inverse: true, data: categories },
      series: groups.map((name) => ({
        name,
        type: line ? 'line' : 'bar',
        connectNulls: false,
        showSymbol: false,
        data: line
          ? categories.map((date) =>
              number(
                marks.find((row) => String(row.bucket) === date && String(row.split ?? 'Unknown') === name)?.[metric],
              ),
            )
          : ordered.map((row) => number(row[metric])),
      })),
    });
    if (onSelect) {
      chart.on('click', (event) => {
        const mark = line
          ? marks.find(
              (row) => String(row.bucket) === String(event.name) && String(row.split ?? 'Unknown') === event.seriesName,
            )
          : ordered[event.dataIndex];
        if (mark) {
          onSelect(mark);
        }
      });
    }
    const resize = new ResizeObserver(() => chart.resize());
    resize.observe(ref.current);
    return () => {
      resize.disconnect();
      chart.dispose();
    };
  }, [rows, metric, line, onSelect, unique]);
  return (
    <Box
      ref={ref}
      role="img"
      aria-label={line ? 'Offers Click Graph' : 'Vendor or offer horizontal ranking'}
      sx={{
        height: line ? 350 : Math.max(280, rows.length * 20),
        maxHeight: 900,
        width: '100%',
        bgcolor: reportPalette.white,
      }}
    />
  );
}
export default function RecoveredWorksheets({
  settled,
  view,
  params,
  onParameter,
  identity,
  onContextChange,
}: {
  settled: SettledComms;
  view: string;
  params: URLSearchParams;
  onParameter: (key: string, value: string) => void;
  identity: string;
  onContextChange: (context: WorksheetContext) => void;
}) {
  const [selections, setSelections] = useState<Record<OfferSelection['source'], OfferSelection | null>>({
    table: null,
    graph: null,
  });
  const [pages, setPages] = useState<Record<string, number>>({});
  const unique = settled.report.state.filters.mode === 'Unique',
    derived = settled.worksheets.map((row) => worksheetMetrics(row, unique));
  const selected = linkedOfferRows(linkedOfferRows(derived, selections.table), selections.graph);
  const sourceNames = [...new Set(selected.map((row) => String(row.detail ?? 'Unknown')))].sort();
  const metric =
    params.get(view === 'vendors' ? 'all_metric' : 'metric_parameter') ?? (view === 'vendors' ? 'ttv' : 'clicks');
  const sections = [...new Set(selected.map((row) => String(row.sheet)))].map((sheet) => {
    const records = selected.filter((row) => row.sheet === sheet);
    const sum = (key: string) => {
      const values = records.map((row) => number(row[key])).filter((value): value is number => value !== null);
      return values.length ? values.reduce((a, b) => a + b, 0) : null;
    };
    return {
      sheet,
      aggregateRecordCount: records.length,
      clicks: sum(unique ? 'unique_clicks' : 'total_clicks'),
      gm: sum('gm'),
      ttv: sum('ttv'),
    };
  });
  const contextKey = JSON.stringify({ identity, ready: settled.ready, selections, metric, sections });
  useEffect(() => onContextChange(JSON.parse(contextKey)), [contextKey, onContextChange]);
  const select = (source: OfferSelection['source'], values: Row) => {
    if (!settled.ready) {
      return;
    }
    setSelections((previous) => ({
      ...previous,
      [source]: JSON.stringify(previous[source]) === JSON.stringify({ source, values }) ? null : { source, values },
    }));
  };
  const exportRows = (sheet: string, rows: Array<Row>) => {
    if (!settled.ready || !settled.report.ready) {
      return;
    }
    const content = worksheetCsv(rows, unique, [
      'Scope',
      sheet,
      'AUD',
      'sourceAsOf=' + (settled.report.sourceAsOf ?? 'Unknown'),
      'effectiveRequest=' + JSON.stringify(settled.scope),
      'linkedActions=' + JSON.stringify(selections),
    ]);
    const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' })),
      link = document.createElement('a');
    link.href = url;
    link.download = 'customer-comms-' + sheet + '.csv';
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const control = (key: string, label: string, options?: Array<string>) => (
    <TextField
      key={key}
      size="small"
      select={!!options}
      type={key.endsWith('_from') || key.endsWith('_to') ? 'date' : 'text'}
      label={label}
      value={
        params.get(key) ??
        String(
          settled.scope[key] ??
            (key === 'all_metric'
              ? 'ttv'
              : key === 'metric_parameter'
                ? 'clicks'
                : key === 'click_split'
                  ? 'product_name'
                  : key === 'vendor_set'
                    ? 'In'
                    : key === 'vendor_mode'
                      ? 'vendor_name'
                      : ''),
        )
      }
      onChange={(event) => onParameter(key, event.target.value)}
      InputLabelProps={{ shrink: true }}
      // The label shares the input's font size so the outline notch (sized from the input font) fits the whole label.
      // minWidth sits on the input because the theme resets min-width on Stack children.
      sx={{
        '& .MuiInputLabel-root': { fontSize: 11 },
        '& .MuiInputBase-root': { fontSize: 11, height: 30, minWidth: controlWidth(label) },
      }}
    >
      {options?.map((value) => (
        <MenuItem key={value} value={value}>
          {OPTION_LABELS[value] ?? value}
        </MenuItem>
      ))}
    </TextField>
  );
  const dateTitle = derived.find((row) => row.sheet === 'Date Title');
  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" gap={1} flexWrap="wrap" sx={{ py: 1 }}>
        {control(view === 'vendors' ? 'all_metric' : 'metric_parameter', 'Metric', ['clicks', 'ttv', 'gm'])}
        {view === 'vendors'
          ? control('vendor_mode', 'Vendor vs Offer Name', ['vendor_name', 'offer_name'])
          : control('pane_split', 'Data Split | Clicks', ['All', 'utm_source', 'utm_content', 'utm_position'])}
        {view === 'clicks' && (
          <>
            {control('pane_from', 'Send Date (Range) from')}
            {control('pane_to', 'Send Date (Range) to')}
            {control('vendor_mode', 'Vendor vs Offer Name', ['vendor_name', 'offer_name'])}
            {control('vendor_set', "Exclude 'Null'", ['In', 'Out', 'All'])}
          </>
        )}
        {view === 'offers' && (
          <Button
            disabled={!selections.table && !selections.graph}
            onClick={() => setSelections({ table: null, graph: null })}
          >
            Clear linked Offer selection
          </Button>
        )}
        {control('message_input', "Message Name 'Custom'", ['All', 'te_australia'])}
      </Stack>
      {view === 'clicks' && (
        <Stack direction="row" gap={1} sx={{ pb: 1 }}>
          {control('click_split', 'Data Split | Clicks By Date', [
            'product_name',
            'utm_content',
            'utm_position',
            'utm_source',
            'utm_campaign',
            'All',
          ])}
        </Stack>
      )}
      {dateTitle && (
        <Typography sx={{ fontSize: 11 }}>
          Source send dates: {String(dateTitle.bucket)} to {String(dateTitle.split)}
        </Typography>
      )}
      <Box
        sx={{
          display: 'grid',
          gap: 1,
          gridTemplateColumns: {
            xs: 'minmax(0,1fr)',
            md:
              view === 'regions'
                ? 'repeat(3,minmax(0,1fr))'
                : view === 'clicks'
                  ? 'repeat(2,minmax(0,1fr))'
                  : 'minmax(0,1fr)',
          },
        }}
      >
        {(WORKSHEET_ROLES[view] ?? []).map((sheet) => {
          const rows = selected.filter((row) => row.sheet === sheet),
            table = sheet !== 'Offers Click Graph' && sheet !== 'Offer Vendor';
          const page = Math.min(pages[sheet] ?? 0, Math.max(0, Math.ceil(rows.length / 50) - 1));
          const dimensions =
            sheet === 'Offers Clicks Table'
              ? ['send_date', 'offer_label', 'vendor_name', 'deal_tier', 'email_placement']
              : ['bucket', 'split', 'detail'];
          return (
            <Box
              key={sheet}
              component="section"
              aria-label={sheet}
              sx={{
                minWidth: 0,
                border: 1,
                borderColor: 'divider',
                p: 1,
                ...(view === 'regions' && sheet === 'GM by Vendor (2)' ? { gridColumn: '1 / -1', order: -1 } : {}),
              }}
            >
              <Stack direction="row" justifyContent="space-between">
                <Typography component="h2" sx={{ fontSize: 12, fontWeight: 600 }}>
                  {view === 'regions'
                    ? sheet === 'GM by Vendor (2)'
                      ? `Vendor ${metric === 'clicks' ? 'Clicks' : metric.toUpperCase()}`
                      : sheet.replace(
                          'GM by Vendor',
                          `${metric === 'clicks' ? 'Clicks' : metric.toUpperCase()} by Vendor`,
                        )
                    : sheet}
                </Typography>
                <Button size="small" disabled={!settled.ready} onClick={() => exportRows(sheet, rows)}>
                  Export worksheet CSV
                </Button>
              </Stack>
              {view === 'regions' ? (
                <RegionalRanking
                  rows={sheet === 'GM by Vendor (2)' ? rows.map((row) => ({ ...row, split: 'Grand Total' })) : rows}
                  metric={metric}
                  sourceNames={sourceNames}
                />
              ) : table ? (
                <>
                  <TableContainer sx={{ maxHeight: 500 }}>
                    <Table
                      stickyHeader
                      size="small"
                      sx={{
                        '& th,& td': { px: 0.75, py: 0.4, fontSize: 10, whiteSpace: 'nowrap' },
                        '& th': { bgcolor: reportPalette.teal, color: reportPalette.white },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          {[...dimensions, 'clicks', 'gm', 'conversion', 'gm_per_click', 'ttv'].map((key) => (
                            <TableCell key={key}>{columnTitle(key)}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.slice(page * 50, page * 50 + 50).map((row, i) => (
                          <TableRow key={i} hover>
                            {dimensions.map((key, j) => (
                              <TableCell key={key}>
                                {sheet === 'Offers Clicks Table' && j === 0 ? (
                                  <Button
                                    size="small"
                                    disabled={!settled.ready}
                                    onClick={() => select('table', row)}
                                    sx={{ fontSize: 10, p: 0 }}
                                  >
                                    {String(row[key] ?? 'Unknown')}
                                  </Button>
                                ) : (
                                  String(row[key] ?? 'Unknown')
                                )}
                              </TableCell>
                            ))}
                            {['clicks', 'gm', 'conversion', 'gm_per_click', 'ttv'].map((key) => (
                              <TableCell key={key} align="right">
                                {formatted(
                                  row[key],
                                  key === 'conversion'
                                    ? 'percent'
                                    : money(key) || key === 'gm_per_click'
                                      ? 'money'
                                      : 'number',
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={rows.length}
                    page={page}
                    rowsPerPage={50}
                    rowsPerPageOptions={[50]}
                    onPageChange={(_, page) => setPages((previous) => ({ ...previous, [sheet]: page }))}
                  />
                </>
              ) : (
                <WorksheetChart
                  unique={unique}
                  rows={rows}
                  metric={metric}
                  line={sheet === 'Offers Click Graph'}
                  onSelect={
                    sheet === 'Offers Click Graph' && settled.ready
                      ? (row) => select('graph', { split: row.split, bucket: row.bucket })
                      : undefined
                  }
                />
              )}
              {!rows.length && (
                <Typography sx={{ fontSize: 11 }}>
                  {settled.ready
                    ? 'No rows for these filters.'
                    : settled.problem
                      ? 'This worksheet could not be loaded.'
                      : 'Loading this worksheet…'}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
